import type { TechnicianDeadline, UpcomingDeadline } from '../../lib/api/domains/customerDeadlines';
import {
  DEADLINE_CATEGORY_LABELS,
  DEADLINE_STATUS_META,
} from '../../lib/customerDeadlines';
import type { CsvValue } from '../../lib/csvExport';

/**
 * CSV-Aufbau für „Verträge & Lizenzen" im Kunden-Hub.
 *
 * Zwei Fassungen, weil dieselbe Liste zwei Empfänger hat:
 * - `customer`: genau das, was der Kunde im Portal sieht — ohne interne Posten,
 *   ohne Stückzahl und ohne interne Notiz. Diese Datei darf weitergegeben werden.
 * - `internal`: die vollständige Liste inkl. Menge, Portal-Kennzeichen und interner
 *   Notiz — nur für den eigenen Gebrauch.
 */

export type ContractCsvMode = 'customer' | 'internal';

/** Inhaber wie im Portal-Export (`OWNERSHIP_LABEL` in customerPortalDeadlines.ts). */
const OWNERSHIP_CSV: Record<string, string> = {
  FIXBYTE: 'von FixByte bereitgestellt',
  CUSTOMER_MANAGED: 'eigener Vertrag, von FixByte verwaltet',
};

const BASE_HEADER: CsvValue[] = [
  'Art',
  'Bezeichnung',
  'Anbieter',
  'Referenz',
];

const TERM_HEADER: CsvValue[] = [
  'Laufzeitbeginn',
  'Laufzeitende',
  'Auto-Verlängerung',
  'Verlängerung (Monate)',
  'Kündigungsfrist (Tage)',
  'Handlungsfrist',
  'Status',
  'Inhaber',
];

function headerFor(mode: ContractCsvMode): CsvValue[] {
  return [
    ...BASE_HEADER,
    ...(mode === 'internal' ? ['Menge'] : []),
    ...TERM_HEADER,
    ...(mode === 'internal' ? ['Im Portal', 'Fristen-Ticket'] : []),
    'Hinweis',
    ...(mode === 'internal' ? ['Interne Notiz'] : []),
  ];
}

function rowFor(d: TechnicianDeadline, mode: ContractCsvMode): CsvValue[] {
  // Ohne eigenes Laufzeitende (Laufzeit angefragt, Managed Service) gibt es keine
  // Frist und keine Ampel — dort steht der Zustand als Text, die Datumsspalten bleiben leer.
  const fixed = d.termType === 'FIXED' ? d : null;
  const service = d.termType === 'SERVICE_CONTRACT';
  return [
    DEADLINE_CATEGORY_LABELS[d.category] ?? d.category,
    d.name,
    d.provider ?? '',
    d.reference ?? '',
    ...(mode === 'internal' ? [d.quantity ?? ''] : []),
    d.startDate ?? '',
    fixed ? fixed.termEndDate : service ? 'mit FixByte-Servicevertrag' : 'Laufzeit angefragt',
    service ? '' : d.autoRenew ? 'ja' : 'nein',
    service ? '' : (d.renewalMonths ?? ''),
    service ? '' : (d.noticeDays ?? ''),
    fixed ? fixed.actionDate : '',
    fixed ? DEADLINE_STATUS_META[fixed.status].label : service ? 'Servicevertrag' : 'Laufzeit angefragt',
    d.createdByCustomer ? 'vom Kunden erfasst' : (OWNERSHIP_CSV[d.ownership] ?? d.ownership),
    ...(mode === 'internal' ? [d.visibleInPortal ? 'ja' : 'nein', d.reminderTicketNumber ?? ''] : []),
    d.customerNote ?? '',
    ...(mode === 'internal' ? [d.internalNote ?? ''] : []),
  ];
}

/**
 * Kopfzeile mit Absender, Empfänger und Stand — dieselbe Form wie der CSV-Export
 * im Kundenportal, damit beide Dateien gleich aussehen.
 */
function titleRow(customerName: string, mode: ContractCsvMode): CsvValue[] {
  const stand = new Date().toLocaleDateString('de-DE');
  return [
    mode === 'internal'
      ? 'FixByte IT – Verträge & Lizenzen (intern)'
      : 'FixByte IT – Verträge & Lizenzen',
    customerName,
    `Stand ${stand}`,
  ];
}

export function contractsCsvRows(
  list: TechnicianDeadline[],
  customerName: string,
  mode: ContractCsvMode
): CsvValue[][] {
  // Die Kundenfassung zeigt nur, was auch im Portal steht („intern" bleibt intern).
  const visible = mode === 'internal' ? list : list.filter((d) => d.visibleInPortal);
  return [
    titleRow(customerName, mode),
    headerFor(mode),
    ...visible.map((d) => rowFor(d, mode)),
  ];
}

/** Kundenübergreifende Liste „Demnächst fällig" — immer interne Fassung. */
export function upcomingCsvRows(list: UpcomingDeadline[], days: number): CsvValue[][] {
  const header: CsvValue[] = [
    'Status',
    'Handlungsfrist',
    'Tage bis Frist',
    'Kunde',
    'Posten',
    'Art',
    'Anbieter',
    'Menge',
    'Referenz',
    'Laufzeitende',
    'Inhaber',
  ];
  return [
    ['FixByte IT – Demnächst fällige Verträge & Lizenzen', `nächste ${days} Tage`, `Stand ${new Date().toLocaleDateString('de-DE')}`],
    header,
    ...list.map((d) => [
      DEADLINE_STATUS_META[d.status].label,
      d.actionDate,
      d.daysLeft,
      d.customer.name,
      d.name,
      DEADLINE_CATEGORY_LABELS[d.category] ?? d.category,
      d.provider ?? '',
      d.quantity ?? '',
      d.reference ?? '',
      d.termEndDate,
      OWNERSHIP_CSV[d.ownership] ?? d.ownership,
    ]),
  ];
}
